import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useUpdateOrganizationUser } from '@/modules/users/hooks/useOrganizationUsers';
import { OrganizationUserDto, UserRole } from '@cargovibe/shared-types';
import { formatFullName } from '@/shared/services/formatFullName';

interface Props {
  user: OrganizationUserDto | null;
  onClose: () => void;
}

interface EditUserFormData {
  firstname: string;
  lastname: string;
  email: string;
  mobileNumber: string;
  role: UserRole;
  isActive: boolean;
  maxBookingsPerMonth: string;
}

function isUserRole(value: string): value is UserRole {
  return Object.values<string>(UserRole).includes(value);
}

export function EditUserDialog({ user, onClose }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const updateMutation: ReturnType<typeof useUpdateOrganizationUser> = useUpdateOrganizationUser();

  const [formData, setFormData] = useState<EditUserFormData>({
    firstname: user?.firstname ?? '',
    lastname: user?.lastname ?? '',
    email: user?.email ?? '',
    mobileNumber: user?.mobileNumber ?? '',
    role: user?.role ?? UserRole.Driver,
    isActive: user?.isActive ?? true,
    maxBookingsPerMonth: String(user?.maxBookingsPerMonth ?? 0),
  });

  const patch = (fields: Partial<EditUserFormData>): void => {
    setFormData((previousFormData) => ({ ...previousFormData, ...fields }));
  };

  const handleOpenChange = (open: boolean): void => {
    if (!open) onClose();
  };

  const handleUpdate = (): void => {
    if (!user) return;
    const parsedLimit: number = Number.parseInt(formData.maxBookingsPerMonth, 10);
    if (!Number.isFinite(parsedLimit) || parsedLimit < 0) return;
    updateMutation.mutate(
      {
        userId: user.id,
        data: {
          firstname: formData.firstname,
          lastname: formData.lastname,
          email: formData.email,
          mobileNumber: formData.mobileNumber,
          role: formData.role,
          isActive: formData.isActive,
          maxBookingsPerMonth: parsedLimit,
        },
      },
      { onSuccess: onClose }
    );
  };

  const handleFirstnameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    patch({ firstname: event.target.value });
  };

  const handleLastnameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    patch({ lastname: event.target.value });
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    patch({ email: event.target.value });
  };

  const handleMobileNumberChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    patch({ mobileNumber: event.target.value });
  };

  const handleRoleChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    if (isUserRole(event.target.value)) {
      patch({ role: event.target.value });
    }
  };

  const handleMaxBookingsChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    patch({ maxBookingsPerMonth: event.target.value });
  };

  const handleActiveToggle = (event: React.ChangeEvent<HTMLInputElement>): void => {
    patch({ isActive: event.target.checked });
  };

  const userName: string = user ? formatFullName(user.firstname, user.lastname) : '';

  return (
    <Dialog open={!!user} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('users.editUser')}</DialogTitle>
          <DialogDescription>{userName}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label htmlFor="edit-firstname" className="text-sm font-medium">
              {t('users.firstname')}
            </label>
            <Input
              id="edit-firstname"
              value={formData.firstname}
              onChange={handleFirstnameChange}
            />
          </div>

          <div className="grid gap-2">
            <label htmlFor="edit-lastname" className="text-sm font-medium">
              {t('users.lastname')}
            </label>
            <Input id="edit-lastname" value={formData.lastname} onChange={handleLastnameChange} />
          </div>

          <div className="grid gap-2">
            <label htmlFor="edit-email" className="text-sm font-medium">
              {t('users.email')}
            </label>
            <Input
              id="edit-email"
              type="email"
              value={formData.email}
              onChange={handleEmailChange}
            />
          </div>

          <div className="grid gap-2">
            <label htmlFor="edit-mobile" className="text-sm font-medium">
              {t('users.mobileNumber')}
            </label>
            <Input
              id="edit-mobile"
              value={formData.mobileNumber}
              onChange={handleMobileNumberChange}
            />
          </div>

          <div className="grid gap-2">
            <label htmlFor="edit-role" className="text-sm font-medium">
              {t('users.role')}
            </label>
            <select
              id="edit-role"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              value={formData.role}
              onChange={handleRoleChange}
            >
              <option value={UserRole.Driver}>{t('users.roles.driver')}</option>
              <option value={UserRole.CompanyAdmin}>{t('users.roles.company_admin')}</option>
            </select>
          </div>

          <div className="grid gap-2">
            <label htmlFor="edit-booking-limit" className="text-sm font-medium">
              {t('users.bookingLimit')}
            </label>
            <Input
              id="edit-booking-limit"
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={formData.maxBookingsPerMonth}
              onChange={handleMaxBookingsChange}
            />
            <p className="text-xs text-muted-foreground">{t('users.bookingLimitHint')}</p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="edit-isActive"
              checked={formData.isActive}
              onChange={handleActiveToggle}
              className="h-4 w-4 rounded border-border"
            />
            <label htmlFor="edit-isActive" className="text-sm font-medium">
              {t('users.activeStatus')}
            </label>
          </div>
        </div>

        {updateMutation.isError && (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {updateMutation.error.message}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={updateMutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
            {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
